import { FC, useEffect, useRef, useState } from 'react'
import { Button, FetchState, Label, TextEditor, useModal } from '../../../../../../components'
import { ArquivoDto, AtendimentoView } from '../../../../atendimento/core/_models'
import {
  dispositivosLabels,
  DispositivoType,
  GenderType,
  IconPaths,
  KTSVG,
  ModalSize,
  QueryState,
  showError,
  OptionsHelper,
  stringifyRequestQuery,
} from '../../../../../../../_metronic/helpers'
import { IData, Leitura } from '../../../atendimento-video/modal'

import { getDiagnostic } from '../../core/_requests'
import { ResultadoIAModal, ResultadoIAModalRef, ResultJSON } from './IA/ResultadoIAModal'

import { downloadAnexo } from '../../../../atendimento/core/_requests'
import { getExames, getMedicamentos } from '../../../prescricao/core/_requests'
import { FormularioAtendimentoView } from '../../../../avaliacoes/avaliacoes-list/core/_models'

type IDiagnosticoModal = {
  onClose: (resultJson: ResultJSON, prompt: string) => void
  setIALoading: (value: boolean) => void
}
export const DiagnosticoIAModal: FC<IDiagnosticoModal> = ({ onClose, setIALoading }) => {
  const resultadoIARef = useRef<ResultadoIAModalRef>(null)

  const { fetchStates, closeModal, showModal } = useModal()
  const { data: atendimento } = (fetchStates['atendimento'] || {}) as FetchState<AtendimentoView>
  const { data: formularioAtendimento } = (fetchStates['formularioAtendimento'] ||
    {}) as FetchState<FormularioAtendimentoView>

  const [prompt, setPrompt] = useState<string>('')
  const [dispositivosData, setDispositivosData] = useState<IData[]>()

  const allDevices = Object.values(DispositivoType)
    .filter((tipo) => tipo !== DispositivoType.Estetoscopio)
    .map((tipo) => ({
      tipo,
      descricao: dispositivosLabels[tipo],
    }))

  // `atendimento` vem dos fetchers do modal: o conteúdo só monta depois do fetch e não há refetch
  // enquanto o modal está aberto, então o prompt é gerado uma única vez.
  useEffect(() => {
    const buildDispositivosData = async () => {
      const informacoes = atendimento?.informacoes || []
      const dispAux: IData[] = []

      for (const informacao of informacoes) {
        const anexo = informacao.anexo
          ? await downloadAnexo(atendimento?.id, informacao.anexo)
          : null

        if (anexo && anexo.type?.startsWith('video/')) {
          continue
        }

        const leitura: Leitura = {
          descricao: informacao.descricao,
          anexoId: informacao.anexo,
          anexo,
          valor: informacao.valor,
          unidade: informacao.unidade,
          dataFormatada: informacao.dataFormatada,
          data: new Date(informacao.data),
        }

        const dispositivoId = informacao.dispositivo?.id
        let info = dispAux.find((d) => d?.dispositivo?.id === dispositivoId)

        if (info) {
          const leituraMap = new Map<string, Leitura>()
          info.leituras.forEach((l) => leituraMap.set(l.descricao, l))
          const existingLeitura = leituraMap.get(leitura.descricao)
          if (!existingLeitura || existingLeitura.data < leitura.data)
            leituraMap.set(leitura.descricao, leitura)
          info.leituras = Array.from(leituraMap.values())
        } else {
          dispAux.push({
            dispositivo: informacao.dispositivo,
            leituras: [leitura],
          })
        }
      }

      setDispositivosData(dispAux)
    }

    const generatePrompt = async () => {
      const nascimento = atendimento?.paciente?.pessoa?.dataNascimento || null
      const genero =
        atendimento?.paciente?.pessoa?.sexo === GenderType.Feminino ? 'Feminino' : 'Masculino'

      const idade = nascimento
        ? Math.max(new Date().getFullYear() - new Date(nascimento).getFullYear(), 0)
        : 'Idade não disponível'

      const message = `
    <p>
    O paciente tem ${idade} anos, é do sexo ${genero}.
    </p>
    <p>
    Sintomas:
    </p>
`

      setPrompt(message)
      await buildDispositivosData()
    }

    generatePrompt()
  }, [atendimento])

  const htmlToStructuredText = (html: string): string => {
    return html
      .replace(/<strong>(.*?)<\/strong>/gi, '$1')
      .replace(/<li class="ql-indent-1">(.*?)<\/li>/gi, '  - $1\n')
      .replace(/<li[^>]*>(.*?)<\/li>/gi, '- $1\n')
      .replace(/<p[^>]*>(.*?)<\/p>/gi, '$1\n\n')
      .replace(/<[^>]+>/g, '')
      .replace(/\n{2,}/g, '\n\n')
      .trim()
  }

  const generateDiagnostic = async () => {
    setIALoading(true)
    closeModal()

    const plainPrompt = htmlToStructuredText(prompt)

    const dispositivosResumo = dispositivosData
      ?.filter(
        (item) =>
          item.dispositivo &&
          item.dispositivo.tipo !== DispositivoType.Camera &&
          item.dispositivo.tipo !== DispositivoType.Estetoscopio
      )
      ?.map((info) => {
        const leituras = info.leituras
          .map((leitura) => `${leitura.descricao}: ${leitura.valor} ${leitura.unidade || ''}`)
          .join(', ')
        return `${info?.dispositivo?.descricao}: ${leituras}`
      })
      .join('\n')

    const formularioResumo =
      formularioAtendimento && formularioAtendimento.respondido
        ? `Formulário: ${formularioAtendimento.formulario.nome}\n` +
          (formularioAtendimento.respostas ?? [])
            .filter((answer) => answer.valores?.length > 0 || answer.valor)
            .map(
              (answer) =>
                `${answer.campo}: ${
                  answer.valores?.length > 0 ? answer.valores.join(', ') : answer.valor
                }`
            )
            .join('\n')
        : ''

    const content = `
${plainPrompt}

Os sinais vitais registrados na admissão foram:
${dispositivosResumo || 'Nenhum dispositivo com leitura'}

${formularioResumo || 'Sem respostas'}

Com base nos dados acima, descreva o melhor Diagnóstico, Receita, Orientações Médicas e Exames complementares.

Estruture a resposta em JSON com os campos:
- diagnostico (string)
- receita (array de objetos com campos: medicamento, dosagem, quantidade, posologia, administracao)
- orientacoes_medicas (objeto com campos: titulo, orientacoes(string separada por linha sem enumerar))
- exames_complementares (array de objetos com campos: exame e instrucoes)
`

    const anexos: ArquivoDto[] =
      dispositivosData
        ?.filter((data) => data.dispositivo?.tipo === DispositivoType.Camera)
        .flatMap((data) =>
          data.leituras
            .filter((leitura) => leitura.anexo !== null)
            .map((leitura) => leitura.anexo as ArquivoDto)
        ) || []

    try {
    } catch (error) {
      showError(error)
    }

    let response

    try {
      response = await getDiagnostic(Number(atendimento?.id), content, anexos)
    } catch (error) {
      setIALoading(false)
      showError(error)
      return
    }

    const data = response.contentMap as ResultJSON

    const updatedReceita = await Promise.all(
      data.receita.map(async (med) => {
        const state: QueryState = {
          page: 0,
          size: 10,
          // sem dosagem o critério sai fora: a IA manda null e "concentracao~*null*" dava 500 no backend
          search: [`nome:${med.medicamento}`, med.dosagem ? `concentracao~*${med.dosagem}*` : '']
            .filter(Boolean)
            .join(','),
        }

        try {
          const medicamentos = await getMedicamentos(stringifyRequestQuery(state))

          if (medicamentos.data && medicamentos.data.length > 0) {
            const encontrado = medicamentos.data[0]

            return {
              ...med,
              id: encontrado.id,
              receituario: OptionsHelper.receituarios.find(
                (opcao) => opcao.value === encontrado.receituario
              ),
            }
          }

          return {
            ...med,
            receituario: undefined,
          }
        } catch (err) {
          return {
            ...med,
            receituario: undefined,
          }
        }
      })
    )
    const updatedExames = await Promise.all(
      data.exames_complementares.map(async (ex) => {
        const state: QueryState = {
          page: 0,
          size: 10,
          search: `nome~*${ex.exame}*`,
        }

        try {
          const exames = await getExames(stringifyRequestQuery(state))

          if (exames.data && exames.data.length > 0) {
            const encontrado = exames.data[0]

            return {
              ...ex,
              id: encontrado.id,
            }
          }

          return { ...ex }
        } catch (err) {
          return { ...ex }
        }
      })
    )

    data.receita = updatedReceita
    data.exames_complementares = updatedExames

    setIALoading(false)
    onClose(data, prompt)

    showModal({
      title: 'Análise da IA',
      content: (
        <ResultadoIAModal
          resultData={data}
          prompt={prompt}
          onChangeResultData={(data) => onClose(data, prompt)}
          ref={resultadoIARef}
        />
      ),
      size: ModalSize.ExtraLarge,
      onClose: () => resultadoIARef.current?.onClose(),
    })
  }

  return (
    <div>
      <h4 className='fw-bold mb-2'>Dispositivos que serão processados pela IA:</h4>
      <div className='d-flex flex-wrap gap-2 mb-2'>
        {allDevices.map((device) => {
          const hasLeituras = dispositivosData
            ?.filter((item) => item.dispositivo)
            ?.some((d) => d?.dispositivo?.tipo === device.tipo && d.leituras.length > 0)
          return (
            <div
              key={device.tipo}
              className='d-flex align-items-center mb-1'
              style={{
                userSelect: 'none',
                flexBasis: '180px',
                gap: '0.5rem',
              }}
            >
              <KTSVG
                path={IconPaths.Check}
                className={`svg-icon-2 ${hasLeituras ? 'text-success' : 'text-gray-400'}`}
              />
              <span className='fs-6'>{device.descricao}</span>
            </div>
          )
        })}
      </div>
      <Label className='mb-1'>Prompt</Label>
      <TextEditor onChange={setPrompt} value={prompt} height={200} />
      <div className='d-flex align-items-center p-5 rounded bg-info-light text-dark'>
        <KTSVG path={IconPaths.Info} color='blue' className='svg-icon-1 me-2' />
        <span className='fs-6'>
          Adicionar os sintomas ao prompt para a hipótese ficar mais precisa
        </span>
      </div>
      <div className='d-flex flex-end mt-4'>
        <Button className='btn-primary' onClick={generateDiagnostic}>
          Gerar Resposta
        </Button>
      </div>
    </div>
  )
}
